import React, { useCallback } from 'react';
import classes from './style.module.scss';
import Button from 'app/common/Button/index';
import useTranslation from 'app/hooks/useTranslation';
import useForm, { FormValues } from "~/app/modules/form/useForm";
import { IssueDetails, IssuePayload, Machine } from "~/types";
import { useStoreActions, useStoreState } from "~/store";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { notify } from "~/app/helpers/notify";
import useWebSocket from "~/app/hooks/useWebSocket";
import { AddIssueProps } from "~/issues/Add";
import { generateGUID } from '~/app/helpers/guid';
import { IssueTypeRepaired, MAX_ISSUE_FILES, MAX_ISSUE_FILE_SIZE_MB, MAX_ISSUE_IMAGES, MAX_ISSUE_IMAGE_SIZE_MB } from '~/app/data/statuses/issue';
import { checkIsObjectPropertiesEqual } from '~/app/helpers/object';
import useAccessControl from '~/app/hooks/useAccessControl';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import moment from 'moment';

export interface FooterProps {
    onClose: () => void
    showDetails: boolean
    setShowDetails: (boolean) => void
    type: AddIssueProps['type']
    issueDetails?: IssueDetails
    defaultFormValues?: FormValues<IssuePayload>
    onSave?: (id: IssueDetails['id']) => void
    machineId?: Machine['id']
    onAddMore?: () => void
    allowAddMore?: boolean
    isWorkOrderChecklist: boolean
}

export type FooterComponent = React.FC<FooterProps>;

const Footer: FooterComponent = ({ onClose, showDetails, setShowDetails, type, issueDetails, defaultFormValues, onSave, machineId: machineIdProp, onAddMore, allowAddMore, isWorkOrderChecklist }) => {
    const { t } = useTranslation('issue');
    const { syncIssues, syncUpdatedIssueDetail, syncDepartmentChange } = useWebSocket();
    const {checkPermission} = useAccessControl();
    const setIssueRegistrationDatePermission = checkPermission(PERMISSION_GROUPS.setIssueRegistrationDate);
    const { id } = useParams<{ id: Machine['id'] }>();
    const resolvedMachineId = machineIdProp ?? issueDetails?.machineId ?? id;
    const { values, formEvents: { onSubmit }, formState } = useForm<IssuePayload>({ formName: 'addIssue' });
    const saveIssueAction = useStoreActions(actions => actions.issue.saveIssue);
    const issueUpdating = useStoreState(state => state.issue.issueUpdating);
    const updateCheckData = useStoreActions(actions => actions.periodicCheck.updateCheckData);
    const currentCheck = useStoreState(state => state.periodicCheck.currentCheck);
    const MachineFetchAction = useStoreActions(actions => actions.machine.fetchMachine);
    const isMachineOfflineAvailable = useStoreState(state => state.machines.isOfflineAvailable);
    const IssueFetchAction = useStoreActions(actions => actions.issue.fetchIssue);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const issueId = issueDetails?.id ?? null;
    const oldDepartmentId = issueDetails?.issueDepartmentId ?? null;
    const displayTypeInIssues = useStoreState(state => state.machine.displayTypeInIssues);
    const isIssueTypeRequired = useStoreState(state => state.machine.isIssueTypeRequired);

    const nextPreviousClickHandler = useCallback((): void => {
        setShowDetails(!showDetails);
    }, [setShowDetails, showDetails]);

    const saveClickHandler = useCallback((addMore: boolean = false): void => {
        onSubmit(({ formState: { invalid } }) => {
            if (invalid) {
                toast.error(t('error.required'));
                return;
            }
            const rawFiles = Array.isArray(values.files) ? values.files as (string | File)[] : [];
            const rawImages = Array.isArray(values.images) ? values.images as (string | File)[] : [];

            if (!netWorkStatus) {
                const imageList = rawImages.filter((img): img is File => typeof img !== 'string' && !!img);
                const fileList = rawFiles.filter((file): file is File => typeof file !== 'string' && !!file);
                const maxImageBytes = MAX_ISSUE_IMAGE_SIZE_MB * 1024 * 1024;
                const maxFileBytes = MAX_ISSUE_FILE_SIZE_MB * 1024 * 1024;
                if (rawImages.length > MAX_ISSUE_IMAGES || rawFiles.length > MAX_ISSUE_FILES
                    || imageList.some(f => f.size > maxImageBytes)
                    || fileList.some(f => f.size > maxFileBytes)) {
                    toast.error(t('error.invalidData'));
                    return;
                }
            }

            const { files: _omitFiles, images: _omitImages, ...restValues } = values;
            const payload: IssuePayload = {
                ...restValues,
                guid: issueId && issueDetails?.guid ? issueDetails?.guid : generateGUID(),
                ...(issueId && { id: issueId }),
                ...(netWorkStatus && (!issueId || issueDetails?.isCreatedFromOfflineMode) && { isCreatedFromOfflineMode: true }),
                ...(!issueId && setIssueRegistrationDatePermission && {registrationDate: values?.registrationDate ? moment(String(values.registrationDate)).format('YYYY-MM-DD HH:mm:ss') : moment().format('YYYY-MM-DD HH:mm:ss')}),
                ...(!issueId && values.sourceTypeId !== undefined && { sourceTypeId: values.sourceTypeId }),
                ...(rawImages.length ? { images: rawImages } : (issueId ? { images: '' } : { images: [] })),
                ...(issueId ? { updateImages: 1 } : {}),
                ...(rawFiles.length ? { files: rawFiles } : (issueId ? { files: '' } : {})),
                ...(issueId ? { updateFiles: 1 } : {}),
            } as IssuePayload;
            saveIssueAction(payload).then(async (data) => {
                if (currentCheck && !isWorkOrderChecklist) {
                    currentCheck.issues.push(data);
                    currentCheck.status = 2;
                    updateCheckData(currentCheck);
                }
                if (addMore) {
                   onAddMore && onAddMore();
                } else {
                    onClose();
                    onSave && onSave(data.id);
                }
                notify(t(`success.${issueId ? 'issueUpdated' : (Number(data.type) === IssueTypeRepaired ? 'repair' : 'issue')}`), 'success');
                if (netWorkStatus === 0) {
                    if (issueId) {
                        syncUpdatedIssueDetail(issueId);
                        Number(oldDepartmentId) !== Number(values.departmentId) && syncDepartmentChange({ issueId: issueId, oldDepartmentId: oldDepartmentId, newDepartmentId: values.departmentId });
                    } else {
                        syncIssues([data.id])
                    }
                }

                await MachineFetchAction(resolvedMachineId);
                const savedIssueId = data.id ?? issueId;
                if (savedIssueId && (netWorkStatus || isMachineOfflineAvailable(resolvedMachineId))) {
                    await IssueFetchAction(savedIssueId);
                }
            });
        })();
    }, [onSubmit, values, issueId, onAddMore, issueDetails?.guid, issueDetails?.isCreatedFromOfflineMode, netWorkStatus, setIssueRegistrationDatePermission, saveIssueAction, t, currentCheck, onClose, onSave, MachineFetchAction, resolvedMachineId, isMachineOfflineAvailable, IssueFetchAction, updateCheckData, syncUpdatedIssueDetail, oldDepartmentId, syncDepartmentChange, syncIssues, isWorkOrderChecklist]);

    const isDisabled = (): boolean => {
        let isDisabled = true;
        const catId = Number(values?.categoryId || 0);
        const deptId = Number(values?.departmentId || 0);
        const sortType = Number(values?.sortingType || 0);
        const hasDescription = !!(values?.description && String(values.description).trim());

        if (
            catId > 0 && hasDescription
            && !(type === 'issue' && deptId <= 0)
            && !(type === 'issue' && displayTypeInIssues && isIssueTypeRequired && sortType <= 0)
            && !(!issueDetails?.id && setIssueRegistrationDatePermission && !values?.registrationDate)
            && formState.touched && !formState.invalid
        ) {
            isDisabled = false;
        }
        if (issueId && values && defaultFormValues && !isDisabled) {
            const normalize = (obj: any) => ({
                ...obj,
                categoryId: Number(obj.categoryId || 0),
                departmentId: Number(obj.departmentId || 0),
                type: Number(obj.type || 0),
                sortingType: Number(obj.sortingType || 0),
                description: (obj.description || '').trim(),
            });
            const keysToCheck = ["departmentId", "categoryId", "images", "files", "description", "type", ...(displayTypeInIssues ? ["sortingType"] : []), ...(!issueDetails?.id && setIssueRegistrationDatePermission ? ['registrationDate'] : [])];
            isDisabled = checkIsObjectPropertiesEqual(normalize(defaultFormValues), normalize(values), keysToCheck);
        }
        return isDisabled;
    }

    return (
        <div className={`${classes['footer']}`}>
            {(!showDetails || issueId) && <Button
                className={classes['abort']}
                onClick={onClose}>{t('cancel')}</Button>}
            {!showDetails && <Button
                className={classes['submit']}
                disabled={Number(values?.categoryId) > 0 ? false : true}
                onClick={nextPreviousClickHandler}>{t('next')}</Button>}
            {showDetails && !issueId && <Button
                className={`${classes['abort']}`}
                onClick={nextPreviousClickHandler}>{t('previous')}</Button>}
            {showDetails && <Button
                className={classes['submit']}
                disabled={isDisabled()}
                loading={issueUpdating} 
                onClick={() => saveClickHandler(false)}>{t(issueId ? 'save' : 'registerFail')}</Button>}
            {showDetails && !issueId && allowAddMore &&
                <Button className={classes['submit']}
                    disabled={isDisabled()}
                    loading={issueUpdating}
                    onClick={() => saveClickHandler(true)}>
                    {t('saveAndAddMore')}
                </Button>}
        </div>
    );
};

export default Footer
